import React, { useState } from 'react';
import { CheckCircle, CloudArrowUp, Eye, FileCsv, UploadSimple, WarningCircle } from '@phosphor-icons/react';
import { decodeText, parseDatei } from '../../../lib/belegung/parse';
import { baueImportPlan } from '../../../lib/belegung/plan';
import { ladeSnapshot, schreibeBelegImport } from '../../../lib/belegung/api';
import type { BelegDatei, ImportPlan, KautionFelder, MieterNeu, WegFelder } from '../../../lib/belegung/types';
import { FormRahmen } from '../../../components/island/form/FormRahmen';
import { FormAbschnitt } from '../../../components/island/form/FormAbschnitt';
import { FormAblage } from '../../../components/island/form/FormAblage';
import { FormKleinFeld } from '../../../components/island/form/FormFeld';
import { FormAufklapper } from '../../../components/island/form/FormAufklapper';
import { FormErgebnis } from '../../../components/island/form/FormErgebnis';
import { FormKnopfleiste } from '../../../components/island/form/FormKnopfleiste';
import { useFormFehler } from '../../../components/island/form/useFormFehler';
import { fehlerMeldung, passtZuAccept } from '../../../components/island/form/form-logik';
import { ABSCHNITT_FARBE, CHIP, FORM, INNEN, type ChipFarbe } from '../../../components/island/form/form-stil';

const fmtEur = (n: number) => n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
const fmtNum = (n: number) => n.toLocaleString('de-DE');

const ACCEPT = '.csv,text/csv';

type Geld = Pick<MieterNeu, 'kaltmiete' | 'nebenkosten_vorauszahlung' | 'heizkosten' | 'kaution'>;

export default function BelegungImportFormular({ onDone }: { onDone: () => void }) {
  const [dateien, setDateien] = useState<BelegDatei[]>([]);
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [objektWahl, setObjektWahl] = useState<Record<number, string>>({}); // dateiindex -> objektnummer (Korrektur)
  const [ergebnis, setErgebnis] = useState<{ einheiten: number; mieter: number; eigentuemer: number; kautionen: number; fehler: string[] } | null>(null);
  const fehler = useFormFehler();

  const reset = () => { setDateien([]); setPlan(null); setObjektWahl({}); setErgebnis(null); };

  const onFiles = async (files: File[]) => {
    const parsed: BelegDatei[] = [];
    for (const f of files) {
      try {
        const text = decodeText(await f.arrayBuffer());
        parsed.push(parseDatei(f.name, text));
      } catch (e: unknown) {
        parsed.push({ dateiname: f.name, objektnummer: null, rows: [], fehler: 'Datei konnte nicht gelesen werden: ' + fehlerMeldung(e, e) });
      }
    }
    setDateien(parsed); setPlan(null); setObjektWahl({}); setErgebnis(null);
  };

  const dryRun = async () => {
    setBusy(true);
    try {
      const effektiv = dateien.map((d, i) => ({ ...d, objektnummer: objektWahl[i] || d.objektnummer }));
      const snap = await ladeSnapshot(effektiv.map((d) => d.objektnummer || '').filter(Boolean));
      setPlan(baueImportPlan(effektiv, snap));
    } finally { setBusy(false); }
  };

  const schreiben = async () => {
    if (!plan) return;
    setBusy(true);
    fehler.leeren();
    try {
      const res = await schreibeBelegImport(plan);
      if (res.fehler.length) console.warn('Belegung-Import-Fehler:', res.fehler);
      setErgebnis(res); // Pille bleibt offen und zeigt das Ergebnis; onDone() erst beim "Schließen"-Button
    } catch (e: unknown) { fehler.zeige('Fehler: ' + fehlerMeldung(e, 'Unbekannt')); }
    finally { setBusy(false); }
  };

  const alleZugeordnet = plan ? plan.dateien.every((d) => d.objekt_id) : false;

  const quelleFarbe = (quelle: BelegDatei['quelle'] | undefined): ChipFarbe => {
    if (quelle === 'kaution') return 'orange';
    if (quelle === 'eigentuemer') return 'gruen';
    if (quelle === 'mietvertraege') return 'blau';
    return 'gold';
  };

  const quelleLabel = (quelle: BelegDatei['quelle'] | undefined) => {
    if (quelle === 'kaution') return 'Kautionen';
    if (quelle === 'eigentuemer') return 'Eigentümerverträge';
    if (quelle === 'mietvertraege') return 'Mietverträge';
    return 'Belegungsliste';
  };

  const fett = { color: FORM.text } as const;

  return (
    <FormRahmen fehler={fehler.text} knopfleiste={ergebnis ? (
      <FormKnopfleiste haupt={{ label: 'Schließen', icon: CheckCircle, typ: 'button', onClick: () => { onDone(); reset(); } }} />
    ) : (
      <FormKnopfleiste
        abbrechen={{ label: 'Vorschau (Dry-Run)', icon: Eye, onClick: dryRun, laedt: busy, gesperrt: !busy && dateien.length === 0, breit: true }}
        haupt={{ label: 'Import ausführen', icon: UploadSimple, typ: 'button', onClick: schreiben, gesperrt: busy || !plan || !alleZugeordnet }} />
    )}>
      {/* Ergebnis nach Import */}
      {ergebnis && (
        <FormErgebnis icon={ergebnis.fehler.length === 0 ? CheckCircle : WarningCircle}
          verlauf={ergebnis.fehler.length === 0 ? ABSCHNITT_FARBE.gruen.verlauf : ABSCHNITT_FARBE.orange.verlauf}
          toenung={ergebnis.fehler.length === 0 ? 'rgba(95,211,138,0.16)' : 'rgba(255,179,64,0.14)'}>
          <span style={{ maxWidth: 440, fontSize: 14.5, fontWeight: 800, lineHeight: 1.45 }}>
            Import abgeschlossen: {ergebnis.einheiten} Einheiten, {ergebnis.mieter} Mieter, {ergebnis.eigentuemer} Eigentümer, {ergebnis.kautionen} Kautionen geschrieben.
          </span>
          {ergebnis.fehler.length > 0 && (
            <div className="w-full text-left">
              <p style={{ margin: '0 0 4px', fontSize: 12.5, fontWeight: 800, color: FORM.rot }}>{ergebnis.fehler.length} Fehler:</p>
              <ul className="pf-scroll list-disc overflow-y-auto" style={{ maxHeight: 160, margin: 0, paddingLeft: 20, fontSize: 12, fontWeight: 600, lineHeight: 1.5, color: FORM.rot }}>
                {ergebnis.fehler.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </div>
          )}
          {ergebnis.fehler.length === 0 && (
            <span style={{ fontSize: 12.5, fontWeight: 700, color: CHIP.gruen.text }}>Alle Datensätze erfolgreich importiert.</span>
          )}
        </FormErgebnis>
      )}

      {/* Drop-Zone */}
      <FormAblage icon={CloudArrowUp} verlauf={ABSCHNITT_FARBE.gruen.verlauf} accept={ACCEPT} multiple kompakt={dateien.length > 0}
        ariaLabel="CSV-Dateien auswählen" tip="CSV-Dateien auswählen oder hierher ziehen"
        text="Belegungslisten, Mietverträge, Eigentümerverträge oder Kautionen (CSV) hierher oder klicken — mehrere möglich"
        onDateien={(files, quelle) => {
          // Beim Ablegen dieselben Dateitypen zulassen wie der Dateidialog (accept).
          const gueltig = quelle === 'ablage' ? files.filter(f => passtZuAccept(f, ACCEPT)) : files;
          if (gueltig.length) void onFiles(gueltig);
        }} />

      {/* Hochgeladene Dateien */}
      {dateien.length > 0 && (
        <FormAbschnitt titel="Hochgeladene Dateien">
          {dateien.map((d, i) => {
            const c = CHIP[quelleFarbe(d.quelle)];
            return (
              <div key={i} className="flex items-center" style={{ gap: 10, padding: '9px 10px 9px 12px', borderRadius: 15, background: INNEN.flaeche, boxShadow: INNEN.ring }}>
                <span aria-hidden className="flex shrink-0 items-center justify-center" style={{ width: 34, height: 34, borderRadius: 10, background: c.flaeche }}>
                  <FileCsv size={18} weight="fill" color={c.text} />
                </span>
                <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 3 }}>
                  <span className="truncate" style={{ fontSize: 13, fontWeight: 700 }}>{d.dateiname}</span>
                  {d.fehler && <span style={{ fontSize: 11.5, fontWeight: 600, color: FORM.rot }}>{d.fehler}</span>}
                  {!d.fehler && (
                    <span className="flex items-center" style={{ gap: 6 }}>
                      <span style={{ fontSize: 10.5, fontWeight: 800, padding: '2px 8px', borderRadius: 999, color: c.text, background: c.flaeche }}>{quelleLabel(d.quelle)}</span>
                      <span style={{ fontSize: 11.5, fontWeight: 600, color: FORM.text3 }}>{d.rows.length} Zeilen</span>
                    </span>
                  )}
                </div>
                <FormKleinFeld value={objektWahl[i] ?? d.objektnummer ?? ''} breite={86} placeholder="Objektnr."
                  ariaLabel={`Objektnummer für ${d.dateiname}`} tip="Objektnummer korrigieren, falls sie nicht aus der Datei erkannt wurde"
                  onChange={(e) => setObjektWahl({ ...objektWahl, [i]: e.target.value })} />
              </div>
            );
          })}
        </FormAbschnitt>
      )}

      {/* Dry-Run-Vorschau */}
      {plan && !ergebnis && (
        <FormAbschnitt titel="Vorschau" icon={Eye} farbe="blau">
          {plan.dateien.map((d, i) => (
            <div key={i} className="flex flex-col" style={{ gap: 8, padding: 12, borderRadius: 16, background: INNEN.flaeche, boxShadow: INNEN.ring, fontSize: 12, fontWeight: 600, lineHeight: 1.5, color: 'rgba(255,255,255,0.6)' }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, lineHeight: 1.4, color: FORM.text }}>
                {d.dateiname} → {d.objekt_id
                  ? <span style={{ color: CHIP.gruen.text }}>{`Objekt ${d.objektnummer} – ${d.objektBezeichnung}`}</span>
                  : <span style={{ color: FORM.rot }}>kein Objekt zugeordnet</span>}
              </p>
              {d.fehler && <p style={{ margin: 0, color: FORM.rot }}>{d.fehler}</p>}
              {!d.fehler && (
                <>
                  {d.kautionen.length > 0 ? (
                    <p style={{ margin: 0 }}>
                      Kautionen: <b style={fett}>{d.kautionen.filter((k) => k.aktion === 'aktualisieren').length}</b> aktualisiert · {d.kautionen.filter((k) => k.aktion === 'uebersprungen').length} übersprungen
                    </p>
                  ) : d.eigentuemer.length > 0 ? (
                    <p style={{ margin: 0 }}>
                      Einheiten: <b style={fett}>{d.einheiten.filter((e) => e.aktion === 'neu').length}</b> neu · {d.einheiten.filter((e) => e.aktion === 'aktualisieren').length} aktualisiert
                      {' | '}Eigentümer: <b style={fett}>{d.eigentuemer.filter((x) => x.aktion === 'neu').length}</b> neu · {d.eigentuemer.filter((x) => x.aktion === 'aktualisieren').length} aktualisiert · {d.eigentuemer.filter((x) => x.aktion === 'wechsel').length} Wechsel
                    </p>
                  ) : (
                    <p style={{ margin: 0 }}>
                      Einheiten: <b style={fett}>{d.einheiten.filter((e) => e.aktion === 'neu').length}</b> neu · {d.einheiten.filter((e) => e.aktion === 'aktualisieren').length} aktualisiert
                      {' | '}Mieter: <b style={fett}>{d.mieter.filter((m) => m.aktion === 'neu').length}</b> neu · {d.mieter.filter((m) => m.aktion === 'aktualisieren').length} aktualisiert · {d.mieter.filter((m) => m.aktion === 'wechsel').length} Wechsel · {d.mieter.filter((m) => m.aktion === 'uebersprungen').length} übersprungen
                    </p>
                  )}

                  {(d.mieter.some((m) => m.aktion === 'wechsel' || m.aktion === 'uebersprungen') || d.kautionen.some((k) => k.aktion === 'uebersprungen') || d.eigentuemer.some((x) => x.aktion === 'wechsel')) && (
                    <FormAufklapper titel="Hinweise anzeigen">
                      <ul className="list-disc" style={{ margin: 0, paddingLeft: 20 }}>
                        {d.eigentuemer.filter((x) => x.aktion === 'wechsel').map((x, k) => <li key={'ew' + k}>Eigentümerwechsel {x.einheitBezeichnung}: {x.alterEigentuemerName} → {x.anzeigeName}</li>)}
                        {d.mieter.filter((m) => m.aktion === 'wechsel').map((m, k) => <li key={'w' + k}>Mieterwechsel: {m.alterMieterName} → {m.anzeigeName} ({m.einheitBezeichnung})</li>)}
                        {d.mieter.filter((m) => m.aktion === 'uebersprungen').map((m, k) => <li key={'s' + k}>Übersprungen: {m.anzeigeName} — {m.grund}</li>)}
                        {d.kautionen.filter((k) => k.aktion === 'uebersprungen').map((k, idx) => <li key={'k' + idx}>Übersprungen: {k.anzeigeName} — {k.grund}</li>)}
                      </ul>
                    </FormAufklapper>
                  )}

                  {(() => {
                    const geld = d.mieter
                      .filter((m) => m.aktion !== 'uebersprungen')
                      .map((m) => ({ name: m.anzeigeName, q: ((m.aktion === 'aktualisieren' ? m.felder : m.neu) || {}) as Geld }))
                      .filter((x) => x.q.kaltmiete != null || x.q.nebenkosten_vorauszahlung != null || x.q.heizkosten != null || x.q.kaution != null);
                    const fl = d.einheiten.filter((e) => e.wohnflaeche != null || e.nutzflaeche != null || e.zimmer != null);
                    const weg = d.eigentuemer
                      .map((x) => ({ name: x.anzeigeName, einheit: x.einheitBezeichnung, w: x.weg as WegFelder }))
                      .filter((x) => x.w.mea != null || x.w.hausgeld_vorschuss != null || x.w.ruecklage_vorschuss != null || x.w.sev_status);
                    const kaut = d.kautionen
                      .filter((k) => k.aktion === 'aktualisieren' && k.felder)
                      .map((k) => ({ name: k.anzeigeName, f: k.felder as KautionFelder }))
                      .filter((k) => k.f.kaution != null || k.f.kaution_ist != null || k.f.kaution_saldo != null || k.f.kaution_art);
                    if (geld.length === 0 && fl.length === 0 && weg.length === 0 && kaut.length === 0) return null;
                    return (
                      <FormAufklapper titel="Werte anzeigen">
                        <div className="flex flex-col" style={{ gap: 4 }}>
                          {fl.length > 0 && (
                            <ul className="list-disc" style={{ margin: 0, paddingLeft: 20 }}>
                              {fl.map((e, k) => (
                                <li key={'e' + k}>{e.bezeichnung}: {e.typ || '—'}
                                  {e.wohnflaeche != null && ` · Wohnfl. ${fmtNum(e.wohnflaeche)} m²`}
                                  {e.nutzflaeche != null && ` · Nutzfl. ${fmtNum(e.nutzflaeche)} m²`}
                                  {e.zimmer != null && ` · ${fmtNum(e.zimmer)} Zi.`}
                                </li>
                              ))}
                            </ul>
                          )}
                          {geld.length > 0 && (
                            <ul className="list-disc" style={{ margin: 0, paddingLeft: 20 }}>
                              {geld.map((x, k) => (
                                <li key={'g' + k}>{x.name}:
                                  {x.q.kaltmiete != null && ` Kaltmiete ${fmtEur(x.q.kaltmiete)}`}
                                  {x.q.nebenkosten_vorauszahlung != null && ` · NK ${fmtEur(x.q.nebenkosten_vorauszahlung)}`}
                                  {x.q.heizkosten != null && ` · Heizk. ${fmtEur(x.q.heizkosten)}`}
                                  {x.q.kaution != null && ` · Kaution ${fmtEur(x.q.kaution)}`}
                                </li>
                              ))}
                            </ul>
                          )}
                          {weg.length > 0 && (
                            <ul className="list-disc" style={{ margin: 0, paddingLeft: 20 }}>
                              {weg.map((x, k) => (
                                <li key={'w' + k}>{x.einheit} — {x.name}:
                                  {x.w.mea != null && ` MEA ${fmtNum(x.w.mea)}`}
                                  {x.w.hausgeld_vorschuss != null && ` · HG ${fmtEur(x.w.hausgeld_vorschuss)}`}
                                  {x.w.ruecklage_vorschuss != null && ` · RL ${fmtEur(x.w.ruecklage_vorschuss)}`}
                                  {x.w.sev_status && ` · ${x.w.sev_status}`}
                                </li>
                              ))}
                            </ul>
                          )}
                          {kaut.length > 0 && (
                            <ul className="list-disc" style={{ margin: 0, paddingLeft: 20 }}>
                              {kaut.map((k, idx) => (
                                <li key={'kt' + idx}>{k.name}:
                                  {k.f.kaution != null && ` Soll ${fmtEur(k.f.kaution)}`}
                                  {k.f.kaution_ist != null && ` · Ist ${fmtEur(k.f.kaution_ist)}`}
                                  {k.f.kaution_saldo != null && ` · Saldo ${fmtEur(k.f.kaution_saldo)}`}
                                  {k.f.kaution_art && ` · ${k.f.kaution_art}`}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </FormAufklapper>
                    );
                  })()}
                </>
              )}
            </div>
          ))}
        </FormAbschnitt>
      )}
    </FormRahmen>
  );
}
